import React, { useState, useEffect, useCallback } from 'react';
import { AppSettingsProvider } from '@/context/AppSettingsContext';
import { usePageTitle } from '@/hooks/usePageTitle';
import { RefreshCw, Eraser, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { getAllowedEmails } from '@/services/allowedEmails';
import { employeeNamesService } from '@/services/employeeNames';
import { addCategory, removeCategory, getCategories } from '@/services/categories';
import { goodThingsService, clearAllCaches } from '@/services/goodThings';
import { syncService } from '../../services/sync';
import { refreshManager } from '../../services/refreshManager';
import { seedsService, removeSeed, getSeeds } from '@/services/seeds';
import { getAllWorkbookMappings, assignWorkbook, removeWorkbook } from '@/services/plmsService';
import { AuthorizedEmails } from '@/components/admin/AuthorizedEmails';
import { Seed } from '@/types/idea-machine';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { SectionState, EmailUser, CategoryItem, PlmsStatus, WorkbookMapping } from '@/types/admin';
import AdminLayout from '@/components/admin/AdminLayout';
import { AdminSection } from '@/components/admin/AdminSidebar';

// Import our component sections
import UsageMetricsSection from '@/components/admin/UsageMetricsSection';
import CategoryManagementSection from '@/components/admin/CategoryManagementSection';
import SeedManagementSection from '@/components/admin/SeedManagementSection';
import PLMSWorkbookSection from '@/components/admin/PLMSWorkbookSection';
import AddNewUserSection from '@/components/admin/AddNewUserSection';
import PMT2SpreadsheetConfigSection from '@/components/admin/PMT2SpreadsheetConfigSection';
import SOPManagementSection from '@/components/admin/SOPManagementSection';
import SOPSummarySection from '@/components/admin/SOPSummarySection';
import AIDevQuestLessonManagementSection from '@/components/admin/AIDevQuestLessonManagementSection';
import FirestoreDebugSection from '@/components/admin/FirestoreDebugSection';
import GlossaryManagementSection from '@/components/admin/GlossaryManagementSection';
import FeedbackManagementSection from '@/components/admin/FeedbackManagementSection';
import FeedbackNotificationRecipientsSection from '@/components/admin/FeedbackNotificationRecipientsSection';
import AppRolloutSection from '@/components/admin/AppRolloutSection';
import DashboardCacheBackfill from '@/components/admin/DashboardCacheBackfill';
import SophieManagementSection from '@/components/admin/SophieManagementSection';

const SERVICE_ACCOUNT = 'listing-management-sheets@jls-lms.iam.gserviceaccount.com';

const sectionConfig: Record<AdminSection, { title: string; description: string }> = {
  'users': { title: 'Authorized Emails', description: 'Manage user access, permissions, and roles.' },
  'add-user': { title: 'Add New User', description: 'Grant system access to new team members.' },
  'app-rollout': { title: 'App Rollout', description: 'Track and manage application rollout phases.' },
  'metrics': { title: 'Usage Metrics', description: 'View system usage statistics and user engagement trends.' },
  'categories': { title: 'Category Management', description: 'Organize content into logical categories.' },
  'seeds': { title: 'Idea Seeds Management', description: 'Manage creative prompts and inspiration seeds.' },
  'plms': { title: 'PLMS Workbook', description: 'Configure PLMS workbook assignments for users.' },
  'pmt2': { title: 'PMT2 Configuration', description: 'Manage PMT2 spreadsheet settings and mappings.' },
  'sops': { title: 'SOP Management', description: 'Create, edit, and organize Standard Operating Procedures.' },
  'sop-summary': { title: 'SOP Summaries', description: 'View and manage AI-generated summaries of SOPs.' },
  'lessons': { title: 'AI DevQuest Lessons', description: 'Manage educational content and learning modules.' },
  'glossary': { title: 'Glossary Management', description: 'Maintain the system glossary and definitions.' },
  'feedback': { title: 'Feedback', description: 'Review user feedback and suggestions.' },
  'feedback-notif': { title: 'Feedback Recipients', description: 'Configure who receives feedback notifications.' },
  'firestore': { title: 'Firestore Debug', description: 'Debug and inspect raw Firestore data.' },
  'cache': { title: 'Dashboard Cache', description: 'Manage and clear system caches for performance.' },
  'sophie': { title: 'Sophie AI Management', description: 'Manage knowledge base and embeddings for the Sophie assistant.' },
};

const AdminPanelContent: React.FC = () => {
  usePageTitle('Admin Panel');
  const { toast } = useToast();
  
  const [activeSection, setActiveSection] = useState<AdminSection>('users');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [newCategory, setNewCategory] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [newSeeds, setNewSeeds] = useState('');
  const [searchQuery, setSearchQuery] = useState('');


  const [categorySection, setCategorySection] = useState<SectionState>({ error: null, success: null });
  const [seedSection, setSeedSection] = useState<SectionState>({ error: null, success: null });
  const [emailList, setEmailList] = useState<EmailUser[]>([]);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [seeds, setSeeds] = useState<Seed[]>([]);
  const [plmsEmail, setPlmsEmail] = useState('');
  const [plmsWorkbookId, setPlmsWorkbookId] = useState('');
  const [plmsStatus, setPlmsStatus] = useState<PlmsStatus | null>(null);
  const [workbookMappings, setWorkbookMappings] = useState<WorkbookMapping[]>([]);
  
  const loadData = useCallback(async () => {
    try {
      // Get employee names from the service
      const employeeNamesData = await employeeNamesService.getEmployeeNames();
      
      const formattedEmailList = employeeNamesData.map(item => ({
        email: item.email,
        displayName: item.displayName,
        isAdmin: false,
        addedAt: new Date(),
      }));

      // Load manually added emails from Firebase
      const manuallyAddedEmailsData = await getAllowedEmails();
      const formattedManuallyAddedEmails = manuallyAddedEmailsData.map(item => ({
        email: item.email,
        displayName: item.displayName || '',
        isAdmin: item.isAdmin,
        addedAt: item.addedAt,
        isManuallyAdded: item.isManuallyAdded,
        source: 'firebase' as const,
      }));

      // Create map of emails with manually added emails taking priority
      const emailMap: { [email: string]: EmailUser } = {};

      // Add manually added emails to the map first
      formattedManuallyAddedEmails.forEach((email: any) => {
        emailMap[email.email] = email;
      });

      // Add emails from Google Sheets, preserving Firebase display names
      formattedEmailList.forEach((email: any) => {
        if (!emailMap[email.email]) {
          emailMap[email.email] = email;
        }
      });

      // Convert the map to an array and sort it
      const combinedEmailListSorted = Object.values(emailMap).sort((a, b) =>
        (a.email || '').localeCompare(b.email || '')
      );

      setEmailList(combinedEmailListSorted);

      // Load other data
      const [categoriesData, seedsData, mappings] = await Promise.all([
        getCategories(),
        getSeeds(),
        getAllWorkbookMappings()
      ]);

      setCategories(categoriesData || []);
      setSeeds(seedsData || []);
      setWorkbookMappings(mappings || []);
    } catch (error) {
      console.error('Error loading data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Error loading data",
        description: errorMessage,
        variant: "destructive"
      });
    }
  }, [toast]);

  useEffect(() => {
    const initializeData = async () => {
      await loadData();
    };

    initializeData();
  }, [loadData]);

  // Category Management
  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      setCategorySection(prev => ({ ...prev, error: 'Please enter a category name' }));
      return;
    }

    try {
      await addCategory(newCategory);
      setCategorySection(prev => ({ ...prev, success: 'Category added successfully' }));
      setNewCategory('');
      await loadData();
    } catch (error) {
      console.error('Error adding category:', error);
      setCategorySection(prev => ({ ...prev, error: 'Failed to add category' }));
    }
  };

  const handleRemoveCategory = async (categoryId: string) => {
    try {
      await removeCategory(categoryId);
      await loadData();
      setCategorySection(prev => ({ ...prev, success: 'Category removed successfully' }));
    } catch (error) {
      console.error('Error removing category:', error);
      setCategorySection(prev => ({ ...prev, error: 'Failed to remove category' }));
    }
  };

  // Seed Management
  const handleAddSeeds = async () => {
    if (!selectedCategory) {
      setSeedSection(prev => ({ ...prev, error: 'Please select a category' }));
      return;
    }

    if (!newSeeds.trim()) {
      setSeedSection(prev => ({ ...prev, error: 'Please enter at least one seed' }));
      return;
    }

    try {
      const seedArray = newSeeds
        .split('\n')
        .map(seed => seed.trim())
        .filter(seed => seed !== '');

      if (seedArray.length === 0) {
        setSeedSection(prev => ({ ...prev, error: 'Please enter at least one seed' }));
        return;
      }

      await Promise.all(seedArray.map(prompt => 
        seedsService.addSeed(prompt, selectedCategory)
      ));

      setSeedSection(prev => ({ ...prev, success: 'Seeds added successfully' }));
      setNewSeeds('');
      await loadData();
    } catch (error) {
      console.error('Error adding seeds:', error);
      setSeedSection(prev => ({ ...prev, error: 'Failed to add seeds' }));
    }
  };

  const handleRemoveSeed = async (seedId: string) => {
    try {
      await removeSeed(seedId);
      await loadData();
      setSeedSection(prev => ({ ...prev, success: 'Seed removed successfully' }));
    } catch (error) {
      console.error('Error removing seed:', error);
      setSeedSection(prev => ({ ...prev, error: 'Failed to remove seed' }));
    }
  };

  const handleAssignWorkbook = async () => {
    try {
      await assignWorkbook(plmsEmail, plmsWorkbookId);
      setPlmsStatus({
        type: 'success',
        message: 'Workbook ID successfully assigned to user'
      });
      setPlmsEmail('');
      setPlmsWorkbookId('');
      await loadData();
    } catch (error) {
      setPlmsStatus({
        type: 'error',
        message: 'Error assigning workbook ID'
      });
    }
  };

  const handleRemoveWorkbook = async (email: string) => {
    try {
      await removeWorkbook(email);
      setPlmsStatus({
        type: 'success',
        message: 'Workbook mapping removed successfully'
      });
      await loadData();
    } catch (error) {
      setPlmsStatus({
        type: 'error',
        message: 'Error removing workbook mapping'
      });
    }
  };
  const renderContent = () => {
    switch (activeSection) {
      case 'users':
        return (
          <AuthorizedEmails 
            emailList={emailList} 
            onUpdate={loadData}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        );
      case 'add-user':
        return <AddNewUserSection onSuccess={loadData} />;
      case 'app-rollout':
        return <AppRolloutSection />;
      case 'metrics':
        return <UsageMetricsSection />;
      case 'categories':
        return (
          <CategoryManagementSection
            categories={categories}
            seeds={seeds}
            newCategory={newCategory}
            setNewCategory={setNewCategory}
            categorySection={categorySection}
            handleAddCategory={handleAddCategory}
            handleRemoveCategory={handleRemoveCategory}
            handleRemoveSeed={handleRemoveSeed}
          />
        );
      case 'seeds':
        return (
          <SeedManagementSection
            categories={categories}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            newSeeds={newSeeds}
            setNewSeeds={setNewSeeds}
            seedSection={seedSection}
            handleAddSeeds={handleAddSeeds}
          />
        );
      case 'plms':
        return (
          <PLMSWorkbookSection
            plmsEmail={plmsEmail}
            setPlmsEmail={setPlmsEmail}
            plmsWorkbookId={plmsWorkbookId}
            setPlmsWorkbookId={setPlmsWorkbookId}
            plmsStatus={plmsStatus}
            workbookMappings={workbookMappings}
            handleAssignWorkbook={handleAssignWorkbook}
            handleRemoveWorkbook={handleRemoveWorkbook}
            serviceAccount={SERVICE_ACCOUNT}
          />
        );
      case 'pmt2':
        return <PMT2SpreadsheetConfigSection />;
      case 'sops':
        return <SOPManagementSection />;
      case 'sop-summary':
        return <SOPSummarySection />;
      case 'lessons':
        return <AIDevQuestLessonManagementSection isExpanded={true} setIsExpanded={() => {}} />;
      case 'glossary':
        return <GlossaryManagementSection isExpanded={true} setIsExpanded={() => {}} />;
      case 'feedback':
        return <FeedbackManagementSection isExpanded={true} setIsExpanded={() => {}} />;
      case 'feedback-notif':
        return <FeedbackNotificationRecipientsSection isExpanded={true} setIsExpanded={() => {}} />;
      case 'firestore':
        return <FirestoreDebugSection />;
      case 'cache':
        return <DashboardCacheBackfill />;
      case 'sophie':
        return <SophieManagementSection />;
      default:
        return <div>Select a section</div>;
    }
  };

  return (
    <AdminLayout activeSection={activeSection} onSelectSection={setActiveSection}>
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">
              {sectionConfig[activeSection]?.title || 'Admin Panel'}
            </h2>
            <p className="text-muted-foreground">
              {sectionConfig[activeSection]?.description || 'Manage your application settings and content.'}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {/* Search Input for Users Section */}
            {activeSection === 'users' && (
              <div className="relative w-64 mr-2">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-9 bg-background"
                />
              </div>
            )}

            {/* Clear Cache Button */}
            <Button
              variant="outline"
              onClick={() => {
                try {
                  clearAllCaches();
                  toast({
                    title: "Cache Cleared",
                    description: "Successfully cleared all Good Things caches",
                    variant: "default"
                  });
                } catch (error) {
                  console.error('Error clearing cache:', error);
                  toast({
                    title: "Cache Clear Failed",
                    description: "Failed to clear caches. Please try again.",
                    variant: "destructive"
                  });
                }
              }}
              className="gap-2"
            >
              <Eraser className="h-4 w-4" />
              Clear Cache
            </Button>
            
            {/* Sync All Data Button */}
            <Button
              onClick={async () => {
                try {
                  setIsSyncing(true);
                  setSyncStatus(null);
                  
                  refreshManager.startSession();
                  await syncService.forceSync();
                  
                  refreshManager.queueRefresh(async () => {
                    await goodThingsService.forceSync();
                    
                    refreshManager.queueRefresh(async () => {
                      const { sopCache } = await import('../../services/sopCache');
                      sopCache.clearCache();
                      
                      const { sopService } = await import('../../services/firebase/SOPService');
                      await sopService.forceSync();
                      
                      setSyncStatus({ type: 'success', message: 'Sync completed successfully' });
                      toast({
                        title: "Sync Completed",
                        description: "Successfully synced data from Google Sheets to Firebase",
                        variant: "default",
                        action: (
                          <ToastAction altText="Refresh" onClick={() => window.location.reload()}>
                            Click to refresh
                          </ToastAction>
                        ),
                      });
                      
                      await loadData();
                    });
                  });
                  
                  refreshManager.endSession();
                } catch (error) {
                  console.error('Sync error:', error);
                  setSyncStatus({ type: 'error', message: 'Failed to sync data' });
                  toast({
                    title: "Sync Failed",
                    description: "Failed to sync data. Please check the backend logs and try again.",
                    variant: "destructive",
                  });
                } finally {
                  setIsSyncing(false);
                }
              }}
              disabled={isSyncing}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Syncing...' : 'Sync All Data'}
            </Button>
          </div>
        </div>

        {syncStatus && (
          <Alert
            variant={syncStatus.type === 'success' ? 'default' : 'destructive'}
            className="mb-4"
          >
            {syncStatus.message}
          </Alert>
        )}

        {renderContent()}
      </div>
    </AdminLayout>
  );
};

const AdminPanel: React.FC = () => {
  return (
    <AppSettingsProvider>
      <AdminPanelContent />
    </AppSettingsProvider>
  );
};

export default AdminPanel;
